import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, User, Briefcase, Home as HomeIcon, Building2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import PasswordInput from "../components/PasswordInput";
import useSEO from "../hooks/useSEO";

const ROLES = [
  { value: "client", label: "Je cherche un service", icon: HomeIcon, desc: "Trouver des prestataires francophones." },
  { value: "professional", label: "Je suis professionnel", icon: User, desc: "Référencer mes services." },
  { value: "entrepreneur", label: "Je suis entrepreneur", icon: Briefcase, desc: "Présenter mon entreprise." },
  { value: "organism", label: "Je représente un organisme", icon: Building2, desc: "Promouvoir notre mission." },
];

const Req = () => <span className="text-red-500" aria-hidden="true">*</span>;

export default function Register() {
  useSEO({ title: "Créer un compte", description: "Rejoignez DemkaBiz - clients, professionnels, entrepreneurs et organismes francophones du Canada." });
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: params.get("role") || "client" });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const r = params.get("role");
    if (r) setForm((f) => ({ ...f, role: r }));
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      navigate(`/verification-envoyee?email=${encodeURIComponent(u.email)}`, { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid lg:grid-cols-2" data-testid="register-page">
      <div className="flex items-center justify-center p-6 sm:p-10 py-12">
        <form onSubmit={submit} className="w-full max-w-lg space-y-5" data-testid="register-form">
          <div>
            <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">Inscription gratuite</p>
            <h1 className="font-display font-bold text-4xl tracking-tight">Créer un compte</h1>
            <p className="text-muted-foreground mt-2">Quelques infos pour commencer. Les champs marqués <Req /> sont obligatoires.</p>
          </div>

          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 block">Je suis… <Req /></label>
            <div className="grid grid-cols-2 gap-2">
              {ROLES.map((r) => (
                <button
                  type="button"
                  key={r.value}
                  onClick={() => setForm({ ...form, role: r.value })}
                  data-testid={`role-${r.value}`}
                  className={`p-3 rounded-xl border text-left transition-all ${form.role === r.value ? "border-primary bg-secondary" : "border-emerald-900/10 hover:bg-secondary/50"}`}
                >
                  <r.icon className={`w-5 h-5 mb-1 ${form.role === r.value ? "text-primary" : "text-foreground/60"}`} />
                  <div className="font-medium text-sm">{r.label}</div>
                  <div className="text-xs text-muted-foreground">{r.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Nom complet <Req /></label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="register-name" />
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Téléphone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="register-phone" placeholder="Optionnel" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Email <Req /></label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="register-email" autoComplete="email" />
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Mot de passe (min. 6 caractères) <Req /></label>
            <div className="mt-1">
              <PasswordInput
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required minLength={6}
                autoComplete="new-password"
                dataTestId="register-password"
                className="pl-4"
              />
            </div>
          </div>

          <div className="rounded-xl bg-secondary/50 border border-primary/10 p-3 text-xs text-foreground/70 flex items-start gap-2">
            <MailCheck className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            Après l'inscription, un email de vérification vous sera envoyé. Pensez à consulter vos <strong>spams / courriers indésirables</strong> et à ajouter <strong>contact@demka.ca</strong> à vos contacts.
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="register-submit">
            {loading ? "Création…" : "Créer mon compte"} <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-sm text-center text-muted-foreground">
            Déjà un compte ? <Link to="/connexion" className="text-primary font-medium hover:underline">Se connecter</Link>
          </p>
        </form>
      </div>
      <div className="hidden lg:block relative bg-secondary">
        <img
          src="/media/visuels/register-visual.jpg"
          alt="DemkaBiz — Votre plateforme 100% francophone : la solution numérique qui facilite l'établissement et la réussite des personnes entrepreneures au Canada"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
      </div>
    </div>
  );
}
